import React, { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { Trans } from '@lingui/macro';
import { gql, useMutation } from '@apollo/client';
import { Divider, Form, Input, Popconfirm, Switch } from 'antd';
import 'animate.css/animate.min.css';

import { Card, Button } from 'components/AntD';
import { CandidateProfile, CandidateReferencerRelation } from 'types';
import {
  CANDIDATE_EVENT_TYPE,
  CANDIDATE_PUBLIC_PROFILE_SETTINGS_COPY_PUBLIC_URL,
  CANDIDATE_PUBLIC_PROFILE_SETTINGS_PREVIEW_PROFILE,
  CANDIDATE_PUBLIC_PROFILE_SETTINGS_SET_PUBLIC,
} from 'enums/events';
import { getVanityLinkDomain } from 'components/CandidateComponents/constants';
import { copyTextToClipboard } from 'components/SharedFunctions';
import { UPDATE_CANDIDATE_IS_PUBLIC_FIELD } from 'api/candidate_api';
import {
  CANDIDATE_PROFILE_DETAILS_FRAGMENT,
  SHARED_CANDIDATE_DETAILS_FRAGMENT,
} from 'api/fragments';
import { trackEvent } from 'util/analytics_events';
import { Select, SelectProps } from 'components/AntD';
import { hasuraClient } from 'clients/hasuraClient';
import MobileTextToggle from 'components/MobileTextToggle';
import ProfileGraphic from './ProfileGraphic';
import ProfilePic from './ProfilePic';
import '../styles.less';
import './styles.less';

const trackCopyUrlEvent = debounce(() => {
  trackEvent(CANDIDATE_PUBLIC_PROFILE_SETTINGS_SET_PUBLIC, CANDIDATE_EVENT_TYPE);
}, 1000);

const trackSetProfilePublicEvent = debounce(() => {
  trackEvent(CANDIDATE_PUBLIC_PROFILE_SETTINGS_COPY_PUBLIC_URL, CANDIDATE_EVENT_TYPE);
}, 1000);

const trackViewPublicProfileEvent = debounce(() => {
  trackEvent(CANDIDATE_PUBLIC_PROFILE_SETTINGS_PREVIEW_PROFILE, CANDIDATE_EVENT_TYPE);
}, 1000);

export interface ReferencerItem {
  id: number;
  label: string;
  isPublic?: boolean;
}

export interface ProfileCardProps {
  candidateFirstName: string;
  candidateProfile: CandidateProfile;
  candidateReferencerRelations: CandidateReferencerRelation[];
  onChange: SelectProps<ReferencerItem>['onChange'];
  candidatePhoto?: string;
  candidateProfileId: number;
}

export default function ProfileCard({
  candidateFirstName,
  candidateProfile,
  candidatePhoto,
  candidateReferencerRelations,
  onChange,
}: ProfileCardProps): JSX.Element {
  const [updateCandidatePublicStatus] = useMutation(UPDATE_CANDIDATE_IS_PUBLIC_FIELD);
  const graphicSection = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;
  const [vanityTextUrl, setVanityTextUrl] = useState(
    `${getVanityLinkDomain()}/${candidateProfile.vanityLinkText}`
  );
  const contents = useMemo(
    () =>
      candidateReferencerRelations.map(relation => ({
        id: relation.candidateReferencerRelationId,
        label: `${relation.referencerFirstName} ${relation.referencerLastName}`,
        isPublic: relation.isPublic,
      })),
    [candidateReferencerRelations]
  );
  const numSelected = useMemo(
    () => candidateReferencerRelations.reduce((acc, i) => acc + (i.isPublic ? 1 : 0), 0),
    [candidateReferencerRelations]
  );
  const onChangePublicStatus = useCallback(
    async isPublic => {
      try {
        if (isPublic) {
          trackSetProfilePublicEvent();
        }
        await updateCandidatePublicStatus({
          variables: {
            input: {
              isPublic,
            },
          },
          update: (cache, { data: { updateCandidateIsPublic } }) => {
            // update the fragment everywhere in the cache
            cache.writeFragment({
              id: 'SharedCandidateDetails_' + updateCandidateIsPublic.candidateProfileId,
              fragment: SHARED_CANDIDATE_DETAILS_FRAGMENT,
              data: updateCandidateIsPublic,
            });
            cache.writeFragment({
              id: `CandidateProfile_${updateCandidateIsPublic.candidateProfileId}`,
              fragment: CANDIDATE_PROFILE_DETAILS_FRAGMENT,
              data: updateCandidateIsPublic,
            });
          },
        });
      } catch (error) {
        console.error(error);
        alert(`Error updating status: ${(error as Error).message}`);
      }
    },
    [updateCandidatePublicStatus]
  );
  const makePublic = useCallback(() => onChangePublicStatus(true), [onChangePublicStatus]);
  const { isPublic: profileIsPublic, vanityLinkText } = candidateProfile;
  const copyTextToClipboardCallback = useCallback(() => {
    copyTextToClipboard(`${getVanityLinkDomain()}/${vanityLinkText}`);
    trackCopyUrlEvent();
  }, [vanityLinkText]);

  const [updateVanityLinkText] = useMutation(
    gql`
      mutation UpdateVanityLinkText($vanityLinkText: String!, $candidateProfileId: Int!) {
        update_CandidateProfiles_by_pk(
          _set: { vanityLinkText: $vanityLinkText }
          pk_columns: { candidateProfileId: $candidateProfileId }
        ) {
          vanityLinkText
        }
      }
    `,
    {
      client: hasuraClient,
    }
  );

  const [form] = Form.useForm();

  return (
    <>
      <Card className="profile-card">
        <div className="tw-flex tw-w-full tw-items-center tw-flex-wrap">
          <h5 className="tw-flex-grow tw-min-w-0">
            <Trans>Share your profile with the world</Trans>
          </h5>

          {profileIsPublic ? (
            <div className="tw-flex tw-items-center sm:tw-mt-xs">
              <div className="tw-mr-xs tw-whitespace-nowrap">
                <Trans>
                  Profile is public&nbsp;
                  <span role="img" aria-label="celebrate">
                    🎉
                  </span>
                </Trans>
              </div>
              <Switch checked={candidateProfile.isPublic} onChange={onChangePublicStatus} />
            </div>
          ) : (
            <Button
              variant="contained"
              color="primary"
              className="sm:tw-mt-sm"
              onClick={makePublic}
            >
              <MobileTextToggle
                mobileText={<Trans>Make public</Trans>}
                text={<Trans>Make profile public</Trans>}
              />
            </Button>
          )}
        </div>
        <Trans>
          Your public profile contains highlights from your reference data. Share your custom URL on
          your resume, LinkedIn, or job apps. Get noticed!
        </Trans>
        <Divider />
        <div className="tw-flex tw-flex-wrap tw-justify-between">
          <div className="settings-section">
            <div className="tw-flex sm:tw-justify-between tw-flex-col sm:tw-flex-row">
              <ProfilePic candidateFirstName={candidateFirstName} candidatePhoto={candidatePhoto} />
              <div className="flex tw-flex tw-flex-col tw-gap-4 tw-ml-8">
                <h5>{candidateFirstName}&rsquo;s Searchlight</h5>
                <Select<ReferencerItem>
                  id="toggle-references"
                  mode="multiple"
                  placement="bottomRight"
                  // here
                  label={
                    <MobileTextToggle
                      text={
                        <Trans>
                          Showing {numSelected}/{contents.length} References
                        </Trans>
                      }
                      mobileText={<i className="fas fa-filter tw-text-gray-600" />}
                    />
                  }
                  contents={contents}
                  selected={contents.filter(({ isPublic }) => isPublic)}
                  onChange={onChange}
                  disableSearch
                  showCaret
                />
                {profileIsPublic && (
                  <div className="tw-flex tw-justify-between">
                    <Input value={vanityTextUrl} size="small" disabled />
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      className="tw-ml-xs"
                      onClick={copyTextToClipboardCallback}
                    >
                      <Trans>Copy</Trans>
                    </Button>
                  </div>
                )}
                <div className="tw-text-left">
                  <Button
                    type="link"
                    href="/candidate/public-profile"
                    size="small"
                    className="tw-mt-sm tw-inline-block"
                    target="_blank"
                    onClick={trackViewPublicProfileEvent}
                  >
                    <Trans>Preview my profile</Trans>
                  </Button>
                </div>
                <Divider />
                <Trans>Customize Your Profile URL</Trans>
                <div className="tw-flex tw-justify-between">
                  <Form
                    form={form}
                    initialValues={{
                      profile_url: candidateProfile.vanityLinkText,
                    }}
                    onFinish={async values => {
                      await updateVanityLinkText({
                        client: hasuraClient,
                        variables: {
                          vanityLinkText: values['profile_url'],
                          candidateProfileId: candidateProfile.candidateProfileId,
                        },
                      });
                      setVanityTextUrl(`${getVanityLinkDomain()}/${values['profile_url']}`);
                    }}
                  >
                    <Form.Item
                      label="Profile URL"
                      name="profile_url"
                      rules={[
                        {
                          required: true,
                          message: 'A url is required.',
                        },
                        {
                          message: 'There are invalid symbols in your url.',
                          validator: (_, value) => {
                            if (/^[a-zA-Z0-9-._~:@!$'()*+,;]+$/.test(value) || value.length == 0) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject('Error');
                            }
                          },
                        },
                      ]}
                    >
                      <Input id="vanityLinkText" addonBefore={getVanityLinkDomain()} />
                    </Form.Item>
                    <Form.Item>
                      <Popconfirm
                        placement="top"
                        title="Updating your link means the old link will no longer work. Do you want to continue?"
                        onConfirm={() => {
                          form.submit();
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          color="primary"
                          size="small"
                          className="tw-ml-xs"
                          variant="outlined"
                          htmlType="submit"
                        >
                          Update
                        </Button>
                      </Popconfirm>
                    </Form.Item>
                  </Form>
                </div>
                {!profileIsPublic && (
                  <span>
                    <Trans>
                      Your profile is not yet public!&nbsp;
                      <Button
                        variant="text"
                        color="info"
                        className="tw-p-0 tw-inline-flex"
                        onClick={makePublic}
                      >
                        Publish it&nbsp;
                      </Button>
                      to see your custom URL.{' '}
                      <span role="img" aria-label="sparkle">
                        ✨
                      </span>
                    </Trans>
                  </span>
                )}
              </div>
            </div>
          </div>
          {profileIsPublic && (
            <div
              className="animate__animated graphic-section animate__faster animate__slideInRight animate__fadeInRight"
              ref={graphicSection}
            >
              <ProfileGraphic />
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
